import type { Emblem, GuildWarArena } from "../net/messages";
import { elem, styleInstaller } from "../ui/chrome";
import { EMBLEM_BYTES, markNode } from "../ui/emblem";

/** One side of the board: the guild's mark, its name, its head count and its
 *  score, as `GuildWarScoreBoard` holds them (`uiguild.py:403-406,415-437`). */
export interface WarBoardSide {
  readonly guildId: number;
  readonly name: string;
  readonly score: number;
  /** `GetAccumulatedJoinerCount`, or -1 for "not reported" -- the value
   *  `Initialize` starts every side at (`uiguild.py:398-399`). */
  readonly members: number;
}

/**
 * One side's line, exactly as `__RefreshName` writes it (`uiguild.py:469-478`):
 * `"%s %d"` while either head count is still -1, and `"%s(%d) %d"` once both
 * have been reported. Either side missing is enough to keep both on the short
 * form, because the original measures the pair.
 */
export function warBoardLine(side: WarBoardSide, counted: boolean): string {
  return counted
    ? `${side.name}(${side.members}) ${side.score}`
    : `${side.name} ${side.score}`;
}

/** Own guild first, then the opponent. Nothing while no war is running, which
 *  is what `GuildWarScoreBoard` not being opened means (`uiguild.py:402-441`). */
export function warBoardSides(
  arena: GuildWarArena,
  ours: WarBoardSide,
): readonly WarBoardSide[] {
  if (!arena.active) return [];
  // `dwGuildSelf` (`war_map.cpp:632`) names the first side outright, which is
  // how an observer, a member of neither guild, learns it; a frame without it
  // falls back to the guild the board was opened with (`uiguild.py:419`).
  const first = arena.your_guild_id
    ? { guildId: arena.your_guild_id, name: arena.your_guild }
    : ours;
  return [
    { ...first, score: arena.your_score, members: arena.your_members },
    {
      guildId: arena.opponent_guild_id,
      name: arena.opponent,
      score: arena.opponent_score,
      members: arena.opponent_members,
    },
  ];
}

const STYLE_ID = "warboard-style";

//  `SetPosition(10, GetScreenHeight() - 100)` over a 50-high board
//  (`uiguild.py:407,471`): ten in from the left edge, fifty up from the
//  bottom, and out of the way of the chat log above it.
const STYLE = `
.warboard {
  position: fixed;
  left: 10px;
  bottom: 50px;
  z-index: 58;
  padding: 8px 12px;
  border-radius: 10px;
  background: rgba(12, 14, 18, 0.86);
  border: 1px solid rgba(200, 170, 120, 0.24);
  pointer-events: none;
  font: 12px/1.5 system-ui, sans-serif;
  color: #d8dee6;
}
.warboard[hidden] { display: none; }
/* mark.SetPosition(10, 10 + 18*i) with the text at (30, 10 + 18*i)
   (uiguild.py:412-417, 427-432): the mark is 20 px left of its own line, and
   the two lines are 18 px apart. */
.warboard-side {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 18px;
  white-space: nowrap;
}
.warboard-ours { color: #d8c79a; }
`;

/**
 * The guild-war score, on the screen for as long as the war runs rather than
 * inside a panel the player must open (`uiguild.py:387-478`).
 */
export class WarBoard {
  private readonly root: HTMLElement;
  private ours: WarBoardSide = { guildId: 0, name: "", score: 0, members: -1 };
  private arena: GuildWarArena | null = null;
  /** One guild's sign, as the server encoded it, by guild id. */
  private readonly marks = new Map<number, readonly number[]>();
  /** Which ids an emblem has already been asked for, so a redraw of the same
   *  war does not ask again. */
  private readonly asked = new Set<number>();

  constructor(
    host: HTMLElement,
    private readonly onRequestEmblem: (guildId: number) => void = () => {},
  ) {
    const doc = host.ownerDocument;
    styleInstaller(STYLE_ID, STYLE)(doc);
    this.root = elem(doc, "div", "warboard");
    this.root.hidden = true;
    host.append(this.root);
  }

  /** The guild this client is in, which the original reads back from the guild
   *  id the board is opened with (`uiguild.py:419`). */
  setOwnGuild(guildId: number, name: string): void {
    this.ours = { ...this.ours, guildId, name };
    if (this.arena !== null) this.update(this.arena);
  }

  /** One guild's sign, from the `Emblem` frame that answered a request. */
  updateEmblem(frame: Emblem): void {
    if (frame.rgba.length !== EMBLEM_BYTES) {
      this.marks.delete(frame.guild_id);
    } else {
      this.marks.set(frame.guild_id, frame.rgba);
    }
    if (this.arena !== null) this.paint(warBoardSides(this.arena, this.ours));
  }

  update(arena: GuildWarArena): void {
    this.arena = arena;
    const sides = warBoardSides(arena, this.ours);
    for (const side of sides) {
      if (side.guildId === 0 || this.asked.has(side.guildId)) continue;
      this.asked.add(side.guildId);
      this.onRequestEmblem(side.guildId);
    }
    this.paint(sides);
  }

  private paint(sides: readonly WarBoardSide[]): void {
    this.root.hidden = sides.length === 0;
    const doc = this.root.ownerDocument;
    const counted = sides.every((side) => side.members >= 0);
    this.root.replaceChildren(
      ...sides.map((side, index) => {
        const row = elem(
          doc,
          "div",
          index === 0 ? "warboard-side warboard-ours" : "warboard-side",
        );
        row.dataset.guildId = String(side.guildId);
        // `ui.MarkBox` with `SetIndex(guildID)`: a guild with no sign leaves
        // an empty box rather than shifting the name left.
        row.append(markNode(this.marks.get(side.guildId) ?? [], doc));
        row.append(elem(doc, "span", "warboard-name", warBoardLine(side, counted)));
        return row;
      }),
    );
  }
}
